import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { LogOut, User, Target, ChevronRight, X, Package, ChefHat, Scale, Activity, AlertCircle, Trash2, Shield, Crown, CreditCard, RefreshCw, Bell, BookOpen } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import BottomNav from '../components/BottomNav';
import useUserStore from '../store/userStore';
import { authAPI, profileAPI, adminAPI, foodPreferencesAPI } from '../utils/api';
import { BACKEND_BASE_URL } from '../config/backend';
import { toast } from 'sonner';
import { useNavigate, useLocation } from 'react-router-dom';
import FoodPreferencePicker from '../components/FoodPreferencePicker';
import useFoodPreferencesDraft from '../hooks/useFoodPreferencesDraft';
import { setUnsavedChangesGuard, shouldBlockLeave } from '../utils/unsavedChangesGuard';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import CookingPreferencesSelector from '../components/CookingPreferencesSelector';
import { useSubscriptionContext } from '../contexts/SubscriptionContext';
import { FEATURES } from '../services/subscriptionService';
import { Capacitor } from '@capacitor/core';
import usePushNotifications from '../hooks/usePushNotifications';
import useKeyboardInset from '../hooks/useKeyboardInset';
import { invalidateNutritionReadiness } from '../hooks/useNutritionReadiness';
import { settingsSectionFor } from '../utils/nutritionReadiness';
import { checkManualMacros } from '../utils/macroConsistency';
import MacroMismatchNotice from '../components/MacroMismatchNotice';

const goals = [
  'Lose Weight', 'Build Muscle', 'Gain Weight', 'Maintain', 'Improve Health', 'Body Recomposition'
];

// Display-only label override. The stored value stays 'Improve Health' (used by
// macro logic, the meal planner, and saved profiles) — we only soften the
// user-facing text to 'Improve Wellness'.
const goalLabel = (g) => (g === 'Improve Health' ? 'Improve Wellness' : g);

const activityLevels = [
  { value: 'sedentary', label: 'Sedentary (little to no exercise)' },
  { value: 'light', label: 'Light (1-3 days/week)' },
  { value: 'moderate', label: 'Moderate (3-5 days/week)' },
  { value: 'active', label: 'Active (6-7 days/week)' },
  { value: 'very_active', label: 'Very Active (intense daily)' },
];

const workoutTypes = [
  'Strength Training', 'Cardio', 'HIIT', 'Fitness Classes', 'Sports', 'Mobility', 'Other'
];

const Settings = () => {
  const navigate = useNavigate();
  const { user, profile, logout, setProfile } = useUserStore();
  const { tier, isSubscribed, expiresAt, willRenew, manageSubscription, showPaywall, refresh: refreshSubscription, restore: restoreSubscription, reconcileBackend, forceDowngradeToFree, TIERS, canAccess, requireFeature } = useSubscriptionContext();
  const { permission: pushPermission, enablePush } = usePushNotifications();
  const [loading, setLoading] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const [syncingSubscription, setSyncingSubscription] = useState(false);
  const [activeSection, setActiveSection] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showRegenerateModal, setShowRegenerateModal] = useState(false);
  const [pendingChanges, setPendingChanges] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  // Delete Account popup only: while the iOS keyboard is open the fixed overlay ends at the keyboard, so
  // the popup centers in the visible space (scrolling inside it on a short screen) instead of the DELETE
  // field and its buttons sitting behind the keyboard.
  const keyboardInset = useKeyboardInset();
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const isNativePlatform = Capacitor.isNativePlatform();
  const location = useLocation();
  // Food Preferences: canonical groups/concepts, explicit Save, revisioned.
  const foodPrefs = useFoodPreferencesDraft();
  const [prefGroups, setPrefGroups] = useState([]);
  const [pendingLeave, setPendingLeave] = useState(null); // { destination } while the leave dialog is open
  const foodPrefsDirtyRef = useRef(false);
  foodPrefsDirtyRef.current = foodPrefs.dirty;
  // Nutrition setup deep link (/settings?setup=...). The backend's nutrition
  // readiness decides which section is genuinely missing; nothing here
  // guesses or fabricates targets.
  const [nutritionSetup, setNutritionSetup] = useState(null); // null | 'loading' | 'error' | 'recover'
  const [recoverConfirmed, setRecoverConfirmed] = useState(false);
  const nutritionSetupActiveRef = useRef(false);
  
  const [formData, setFormData] = useState({
    // Goal
    goal: '',
    // Body Info
    age: '',
    sex: '',
    units: 'metric',
    height_cm: '',
    weight_kg: '',
    // For imperial display
    heightFeet: '',
    heightInches: '',
    weightLbs: '',
    // Activity
    activity_level: '',
    workout_types: [],
    // Macro Targets
    auto_macros_enabled: true,
    target_calories: '',
    target_protein_g: '',
    target_carbs_g: '',
    target_fat_g: '',
    // Cooking
    cookware: [],
    max_cook_time: null,
  });

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await profileAPI.getProfile();
        const p = response.data;
        
        // Convert metric to imperial for display if needed
        let heightFeet = '', heightInches = '', weightLbs = '';
        if (p.height_cm) {
          const totalInches = p.height_cm / 2.54;
          heightFeet = Math.floor(totalInches / 12).toString();
          heightInches = Math.round(totalInches % 12).toString();
        }
        if (p.weight_kg) {
          weightLbs = Math.round(p.weight_kg * 2.20462).toString();
        }
        
        setFormData({
          // Map "Lose Body Fat" to "Lose Weight" for UI display
          goal: p.goal === 'Lose Body Fat' ? 'Lose Weight' : (p.goal || ''),
          age: p.age || '',
          sex: p.sex || '',
          units: p.units || 'metric',
          height_cm: p.height_cm || p.height || '',
          weight_kg: p.weight_kg || p.weight || '',
          heightFeet,
          heightInches,
          weightLbs,
          activity_level: p.activity_level || '',
          workout_types: (p.workout_types || p.workouts || []).map(w => w.toLowerCase().replace(/\s+/g, '_')),
          auto_macros_enabled: p.auto_macros_enabled !== false, // Default true
          target_calories: p.target_calories || p.calories || '',
          target_protein_g: p.target_protein_g || p.protein || '',
          target_carbs_g: p.target_carbs_g || p.carbs || '',
          target_fat_g: p.target_fat_g || p.fat || '',
          cookware: p.cookware || [],
          max_cook_time: p.max_cook_time || null,
        });
        setProfile(p);
      } catch (error) {
        console.error('Failed to fetch profile:', error);
      }
    };
    
    const checkAdmin = async () => {
      try {
        const res = await adminAPI.checkAdmin();
        setIsAdmin(res.data.is_admin === true);
      } catch (error) {
        console.error('Admin check failed:', error);
      }
    };
    
    fetchProfile();
    checkAdmin();
  }, [setProfile]);

  const openSetupSection = (section) => {
    setActiveSection(section);
    setTimeout(() => {
      const el = document.querySelector('[data-testid="' + section + '-section-toggle"]');
      if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
  };

  // Fetch readiness fresh and open what is genuinely missing. `recover`
  // (Auto Macros on, every input present, targets absent) is re-checked once
  // more before regeneration is offered.
  const runNutritionSetup = async () => {
    nutritionSetupActiveRef.current = true;
    setNutritionSetup('loading');
    setRecoverConfirmed(false);
    invalidateNutritionReadiness();
    try {
      let readiness = (await profileAPI.getNutritionReadiness()).data;
      if (readiness && readiness.next_step === 'recover') {
        setNutritionSetup('recover');
        readiness = (await profileAPI.getNutritionReadiness()).data;
        if (readiness && readiness.next_step === 'recover' && readiness.can_regenerate) {
          setRecoverConfirmed(true);
          return;
        }
      }
      if (!readiness || typeof readiness.next_step !== 'string') throw new Error('bad readiness');
      if (readiness.next_step === 'ready') {
        nutritionSetupActiveRef.current = false;
        setNutritionSetup(null);
        toast.success('Your nutrition targets are set');
        return;
      }
      const section = settingsSectionFor(readiness);
      setNutritionSetup(null);
      if (section) openSetupSection(section);
    } catch (error) {
      setNutritionSetup('error');
    }
  };

  // After a save made during setup, continue to the next missing step.
  const continueNutritionSetup = () => {
    invalidateNutritionReadiness(); // a profile save can change readiness anywhere in the app
    if (nutritionSetupActiveRef.current) runNutritionSetup();
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (!params.get('setup')) return;
    navigate(location.pathname, { replace: true }); // consume the deep link once
    runNutritionSetup();
  }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  // Backend group authority for the Food Preferences picker.
  useEffect(() => {
    let alive = true;
    foodPreferencesAPI.getOptions()
      .then((resp) => { if (alive) setPrefGroups((resp.data && resp.data.groups) || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  // Leaving Settings with unsaved food preferences asks Save / Discard / Keep
  // editing (tab bar, in-page links, Android back). A browser reload/close
  // gets the browser's own confirmation.
  useEffect(() => {
    setUnsavedChangesGuard((destination) => {
      if (!shouldBlockLeave(foodPrefsDirtyRef.current, destination, location.pathname)) return true;
      setPendingLeave({ destination });
      return false;
    });
    const onBeforeUnload = (e) => {
      if (!foodPrefsDirtyRef.current) return undefined;
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      setUnsavedChangesGuard(null);
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, [location.pathname]);

  // true = leave now; false = the Save / Discard dialog took over.
  const allowLeave = (destination) => {
    if (shouldBlockLeave(foodPrefsDirtyRef.current, destination, location.pathname)) {
      setPendingLeave({ destination });
      return false;
    }
    return true;
  };

  const guardedNavigate = (destination) => {
    if (allowLeave(destination)) navigate(destination);
  };

  const finishLeave = () => {
    const target = pendingLeave && pendingLeave.destination;
    setPendingLeave(null);
    if (target == null) window.history.back();
    else navigate(target);
  };

  const handleLeaveSave = async () => {
    const ok = await foodPrefs.save();
    if (ok) {
      toast.success('Food preferences saved');
      finishLeave();
    } else {
      setPendingLeave(null);
      setActiveSection('preferences');
    }
  };

  const handleLeaveDiscard = () => {
    foodPrefs.discard();
    foodPrefsDirtyRef.current = false;
    finishLeave();
  };

  const handleSaveFoodPreferences = async () => {
    const ok = await foodPrefs.save();
    if (ok) toast.success('Food preferences saved');
  };

  const handleLogout = async () => {
    // Single-flight guard: repeated taps while the request is in flight
    // (e.g. during a slow/unindexed backend lookup) must not fire multiple
    // competing POST /auth/logout requests. The disabled button below is
    // the primary defense; this is a defense-in-depth check in case
    // handleLogout is ever invoked programmatically.
    if (loggingOut) return;
    setLoggingOut(true);
    try {
      await authAPI.logout();
    } catch (error) {
      // Server-side revoke failed (network error, already-invalid session,
      // etc.) — fall through to the same local cleanup below regardless.
      // Client auth must always be cleared even when the server call fails.
    } finally {
      logout();
      navigate('/login');
      toast.success('Logged out successfully');
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }
    
    setDeleting(true);
    try {
      await authAPI.deleteAccount();
      logout();
      navigate('/');
      toast.success('Your account has been permanently deleted.');
    } catch (error) {
      console.error('Delete account error:', error);
      toast.error('Failed to delete account. Please try again.');
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
      setDeleteConfirmText('');
    }
  };

  // Helper to convert imperial to metric
  const convertToMetric = () => {
    if (formData.units === 'metric') {
      return {
        height_cm: parseFloat(formData.height_cm) || null,
        weight_kg: parseFloat(formData.weight_kg) || null
      };
    } else {
      const totalInches = (parseInt(formData.heightFeet) || 0) * 12 + (parseInt(formData.heightInches) || 0);
      const heightCm = totalInches > 0 ? totalInches * 2.54 : null;
      const weightKg = formData.weightLbs ? parseFloat(formData.weightLbs) * 0.453592 : null;
      return { height_cm: heightCm, weight_kg: weightKg };
    }
  };

  // Check if changes require macro regeneration
  const shouldTriggerRegenerate = (changes) => {
    const triggerFields = ['goal', 'sex', 'age', 'height_cm', 'weight_kg', 'activity_level', 'workout_types'];
    return triggerFields.some(f => f in changes);
  };

  // Handle goal change with regenerate modal
  const handleUpdateGoal = async () => {
    setLoading(true);
    try {
      // Map "Lose Weight" to "Lose Body Fat" for backend compatibility
      const backendGoal = formData.goal === 'Lose Weight' ? 'Lose Body Fat' : formData.goal;
      const response = await profileAPI.updateProfile({ goal: backendGoal });
      if (response.data.should_regenerate) {
        setPendingChanges({ goal: backendGoal });
        setShowRegenerateModal(true);
      } else {
        toast.success('Goal updated successfully');
        continueNutritionSetup();
      }
      setActiveSection(null);
    } catch (error) {
      toast.error('Failed to update goal');
    } finally {
      setLoading(false);
    }
  };

  // Handle body info changes with regenerate modal
  const handleUpdateBodyInfo = async () => {
    setLoading(true);
    try {
      const { height_cm, weight_kg } = convertToMetric();
      const changes = {
        age: parseInt(formData.age) || undefined,
        sex: formData.sex || undefined,
        units: formData.units,
        height_cm: height_cm ? Math.round(height_cm) : undefined,
        weight_kg: weight_kg ? parseFloat(weight_kg.toFixed(1)) : undefined,
      };
      
      // Remove undefined values
      Object.keys(changes).forEach(k => changes[k] === undefined && delete changes[k]);
      
      const response = await profileAPI.updateProfile(changes);
      
      if (response.data.should_regenerate) {
        setPendingChanges(changes);
        setShowRegenerateModal(true);
      } else {
        toast.success('Body info updated successfully');
        continueNutritionSetup();
      }
      setActiveSection(null);
    } catch (error) {
      toast.error('Failed to update body info');
    } finally {
      setLoading(false);
    }
  };

  // Handle activity changes with regenerate modal  
  const handleUpdateActivity = async () => {
    setLoading(true);
    try {
      const changes = {
        activity_level: formData.activity_level,
        workout_types: formData.workout_types,
      };
      
      const response = await profileAPI.updateProfile(changes);
      
      if (response.data.should_regenerate) {
        setPendingChanges(changes);
        setShowRegenerateModal(true);
      } else {
        toast.success('Activity updated successfully');
        continueNutritionSetup();
      }
      setActiveSection(null);
    } catch (error) {
      toast.error('Failed to update activity');
    } finally {
      setLoading(false);
    }
  };

  // 4/4/9 check of the manual Macro Targets fields (shared with onboarding).
  const manualMacroCheck = checkManualMacros({
    calories: formData.target_calories, protein: formData.target_protein_g,
    carbs: formData.target_carbs_g, fat: formData.target_fat_g,
  });
  const applyDerivedTargetCalories = () => {
    if (manualMacroCheck.expectedCalories === null) return;
    setFormData({ ...formData, target_calories: String(manualMacroCheck.expectedCalories) });
  };

  // Handle macro target changes (only when auto_macros is OFF)
  const handleUpdateMacros = async () => {
    // Check subscription for macro customization
    if (!canAccess(FEATURES.CAN_CUSTOMIZE_MACROS)) {
      requireFeature(FEATURES.CAN_CUSTOMIZE_MACROS);
      return;
    }
    
    if (formData.auto_macros_enabled) {
      toast.error('Turn off Auto Macros to edit manually');
      return;
    }
    // Shared 4/4/9 rule (the server enforces it too): never save an
    // inconsistent manual target set; the notice offers the explicit fix.
    if (!manualMacroCheck.consistent) return;
    
    setLoading(true);
    try {
      await profileAPI.updateProfile({
        auto_macros_enabled: false,
        target_calories: parseInt(formData.target_calories),
        target_protein_g: parseInt(formData.target_protein_g),
        target_carbs_g: parseInt(formData.target_carbs_g),
        target_fat_g: parseInt(formData.target_fat_g),
      });
      toast.success('Macro targets updated');
      setActiveSection(null);
      continueNutritionSetup();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      if (error?.response?.status === 422 && detail?.error === 'macros_inconsistent') {
        toast.error(`Calories should be ${detail.expected_calories} for these macros.`);
        return;
      }
      toast.error('Failed to update macros');
    } finally {
      setLoading(false);
    }
  };

  // Handle auto_macros toggle
  const handleToggleAutoMacros = async (enabled) => {
    // Check subscription for macro customization when turning off auto
    if (!enabled && !canAccess(FEATURES.CAN_CUSTOMIZE_MACROS)) {
      requireFeature(FEATURES.CAN_CUSTOMIZE_MACROS);
      return;
    }

    if (enabled) {
      // Turning ON: never flip the switch locally before a successful
      // backend V4 regeneration. POST /profile/regenerate-targets now
      // writes the new targets AND auto_macros_enabled=true together in one
      // atomic $set — there is no separate mode PATCH to fail independently
      // of the targets write, so a response here means the backend is
      // unambiguously auto with these exact targets. The backend fails
      // closed (422 profile_incomplete_for_regeneration, zero writes) when
      // required body/profile fields are missing — on any failure here the
      // user's existing manual targets and auto_macros_enabled=false remain
      // completely untouched (see AGENTS.md pre-launch audit
      // nutrition-readiness follow-up).
      setLoading(true);
      try {
        const response = await profileAPI.regenerateTargets();
        setFormData(prev => ({
          ...prev,
          auto_macros_enabled: true,
          target_calories: response.data.target_calories,
          target_protein_g: response.data.target_protein_g,
          target_carbs_g: response.data.target_carbs_g,
          target_fat_g: response.data.target_fat_g,
        }));
        toast.success('Macros recalculated based on your profile');
      } catch (error) {
        const missingFields = error?.response?.data?.detail?.missing_fields;
        if (Array.isArray(missingFields) && missingFields.length > 0) {
          toast.error('Add your ' + missingFields.join(', ') + ' before turning on Auto Macros');
        } else {
          toast.error('Failed to regenerate macros');
        }
        // Nothing was flipped locally or on the backend — stay in manual mode.
      } finally {
        setLoading(false);
      }
    } else {
      // Turning OFF - just update the flag. Optimistic local flip is safe
      // here (unlike the ON path) since there's no calculation that could
      // fail and leave stale/invented targets behind.
      setFormData(prev => ({ ...prev, auto_macros_enabled: false }));
      try {
        await profileAPI.updateProfile({ auto_macros_enabled: false });
        toast.success('Manual macro editing enabled');
      } catch (error) {
        toast.error('Failed to update setting');
        // Mirror the ON-path's rollback: on a failed save the backend still
        // has auto_macros_enabled=true and may keep auto-recalculating
        // targets, so the UI must not show manual mode as if it took effect
        // (see AGENTS.md pre-launch audit, finding SET-1).
        setFormData(prev => ({ ...prev, auto_macros_enabled: true }));
      }
    }
  };

  // Handle regenerate modal responses
  const handleRegenerateYes = async () => {
    setLoading(true);
    try {
      // A manual user's body-info edit that triggered this modal is already
      // saved (handleUpdateGoal/handleUpdateBodyInfo/handleUpdateActivity
      // awaited profileAPI.updateProfile before opening it) — their existing
      // manual targets and auto_macros_enabled=false are untouched by that
      // save. POST /profile/regenerate-targets writes the new targets AND
      // auto_macros_enabled=true together in one atomic $set — there is no
      // separate mode PATCH that could fail independently and leave the
      // backend auto_macros_enabled=false with already-changed targets (the
      // split-brain this closes). The backend fails closed (422
      // profile_incomplete_for_regeneration, zero writes) on incomplete
      // inputs, and on ANY failure here the old targets and manual mode
      // remain exactly as they were (see AGENTS.md pre-launch audit
      // nutrition-readiness follow-up).
      const wasAutoOff = formData.auto_macros_enabled === false;
      const response = await profileAPI.regenerateTargets();

      const newMacros = {
        target_calories: response.data.target_calories,
        target_protein_g: response.data.target_protein_g,
        target_carbs_g: response.data.target_carbs_g,
        target_fat_g: response.data.target_fat_g,
        calories: response.data.target_calories,
        protein: response.data.target_protein_g,
        carbs: response.data.target_carbs_g,
        fat: response.data.target_fat_g,
      };
      // Update local form state (including auto_macros if it was flipped)
      setFormData(prev => ({
        ...prev,
        ...newMacros,
        auto_macros_enabled: true,
      }));
      // Update global profile state
      setProfile(prev => ({
        ...prev,
        ...newMacros,
        auto_macros_enabled: true,
      }));
      toast.success(
        wasAutoOff
          ? 'Auto Macros turned on and macros regenerated'
          : 'Macros regenerated based on updated information'
      );
    } catch (error) {
      // Regeneration failed (including a 422 for incomplete profile data) —
      // nothing was persisted or flipped above, so old targets and manual
      // mode remain exactly as they were; local state is untouched too.
      const missingFields = error?.response?.data?.detail?.missing_fields;
      if (Array.isArray(missingFields) && missingFields.length > 0) {
        toast.error('Add your ' + missingFields.join(', ') + ' before regenerating macros');
      } else {
        toast.error('Failed to regenerate macros');
      }
    } finally {
      setLoading(false);
      setShowRegenerateModal(false);
      setPendingChanges(null);
      continueNutritionSetup();
    }
  };

  const handleRegenerateNo = () => {
    toast.success('Profile saved, keeping current macro targets');
    setShowRegenerateModal(false);
    setPendingChanges(null);
    continueNutritionSetup();
  };

  // Manual subscription sync — addresses the split-brain case where the
  // device's RC SDK cache says "Pro" but the backend has the user as free
  // (e.g., a stale sandbox subscription whose webhook never fired). Two
  // steps:
  //   1. Refresh device-side RC SDK from Apple (catches new sandbox state
  //      and "already subscribed" recoveries).
  //   2. Force a backend reconcile against RC's authoritative API.
  // Reports the resulting state via toast so the user knows what happened.
  const handleSyncSubscription = async () => {
    setSyncingSubscription(true);
    try {
      // Step 1: device-side refresh (this can trigger the iOS "Already
      // Subscribed" dialog if the device has lost cache). Best-effort.
      try {
        await restoreSubscription();
      } catch (e) {
        console.warn('[Settings] device-side restore failed (non-fatal):', e?.message || e);
      }
      // Step 2: backend reconcile against RC.
      const result = await reconcileBackend();
      const status = result?.reconcile_status;
      try { await refreshSubscription(); } catch (_) { /* ignore */ }

      if (status === 'ok_paid' || status === 'skipped_override') {
        toast.success('Subscription synced — you\'re on Fitxess Pro.');
      } else if (status === 'ok_free') {
        // Backend confirmed user is truly free. Override local cache so
        // the rest of the app stops behaving as if Pro is active.
        forceDowngradeToFree();
        toast.message('No active subscription found. If you believe this is a mistake, contact support.');
      } else if (typeof status === 'string' && status.startsWith('degraded:')) {
        toast.error('Couldn\'t reach RevenueCat. Please check your connection and try again.');
      } else {
        toast.message('Subscription sync completed.');
      }
    } catch (e) {
      console.error('[Settings] handleSyncSubscription failed:', e);
      toast.error('Sync failed. Please try again.');
    } finally {
      setSyncingSubscription(false);
    }
  };

  const handleUpdateCookingPreferences = async () => {
    setLoading(true);
    try {
      await profileAPI.updateCookingPreferences({
        cookware: formData.cookware,
        max_cook_time: formData.max_cook_time,
      });
      toast.success('Cooking preferences updated successfully');
      setActiveSection(null);
    } catch (error) {
      toast.error('Failed to update cooking preferences');
    } finally {
      setLoading(false);
    }
  };

  const toggleWorkout = (workout) => {
    const normalized = workout.toLowerCase().replace(/\s+/g, '_');
    setFormData(prev => ({
      ...prev,
      workout_types: prev.workout_types.includes(normalized)
        ? prev.workout_types.filter(w => w !== normalized)
        : [...prev.workout_types, normalized]
    }));
  };

  // Format display values
  const getHeightDisplay = () => {
    if (formData.units === 'imperial') {
      return formData.heightFeet && formData.heightInches 
        ? `${formData.heightFeet}'${formData.heightInches}"` 
        : 'Not set';
    }
    return formData.height_cm ? `${formData.height_cm} cm` : 'Not set';
  };

  const getWeightDisplay = () => {
    if (formData.units === 'imperial') {
      return formData.weightLbs ? `${formData.weightLbs} lbs` : 'Not set';
    }
    return formData.weight_kg ? `${formData.weight_kg} kg` : 'Not set';
  };

  return (
    <div className="min-h-screen pb-bottom-nav bg-background">
      {/* `header-safe-top` provides env(safe-area-inset-top) + 8px — single offset.
          No extra spacer; px-6 pb-6 for horizontal + bottom padding only. */}
      <div className="header-safe-top px-6 pb-6 space-y-6">
        <motion.div initial={{ opacity: 0, y: 0 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="font-heading font-bold text-3xl mb-2" data-testid="settings-title">Settings</h1>
          <p className="text-muted-foreground">Manage your profile and preferences</p>
        </motion.div>

        {nutritionSetup && (
          <div className="bg-card border border-border/40 rounded-xl p-4 space-y-3" data-testid="nutrition-setup-banner">
            {nutritionSetup === 'loading' ? (
              <p className="text-sm text-muted-foreground">Checking your nutrition setup…</p>
            ) : (
              <>
                <div className="flex items-start gap-2">
                  <AlertCircle size={18} className="text-amber-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium">We couldn&apos;t load your nutrition targets.</p>
                    {nutritionSetup === 'recover' && recoverConfirmed && (
                      <p className="text-xs text-muted-foreground">
                        Your goal and profile are complete, but your calculated targets are missing. You can regenerate them from your profile.
                      </p>
                    )}
                  </div>
                </div>
                {nutritionSetup === 'error' && (
                  <Button onClick={runNutritionSetup} variant="outline" className="w-full h-10 rounded-full" data-testid="nutrition-setup-retry">
                    Retry
                  </Button>
                )}
                {nutritionSetup === 'recover' && recoverConfirmed && (
                  <Button onClick={handleRegenerateYes} disabled={loading} className="w-full h-10 rounded-full bg-accent text-white" data-testid="nutrition-setup-regenerate">
                    {loading ? 'Regenerating...' : 'Regenerate targets'}
                  </Button>
                )}
              </>
            )}
          </div>
        )}

        {/* Profile Card */}
        <motion.div
          initial={{ opacity: 0, y: 0 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-card border border-border/40 rounded-xl p-6 shadow-sm"
        >
          <div className="flex items-center gap-4">
            {user?.picture ? (
              <img src={user.picture} alt={user.name} className="w-16 h-16 rounded-full" />
            ) : (
              <div className="w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center">
                <User size={32} className="text-accent" />
              </div>
            )}
            <div>
              <h2 className="font-heading font-semibold text-lg">{user?.name}</h2>
              <p className="text-sm text-muted-foreground">{user?.email}</p>
            </div>
          </div>
        </motion.div>

        {/* Subscription Section */}
        <motion.div
          initial={{ opacity: 0, y: 0 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-card border border-border/40 rounded-xl overflow-hidden shadow-sm"
          data-testid="subscription-section"
        >
          <div className="p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isSubscribed ? 'bg-gradient-to-br from-accent to-cyan-400' : 'bg-secondary'}`}>
                  <Crown size={20} className={isSubscribed ? 'text-white' : 'text-muted-foreground'} />
                </div>
                <div>
                  <p className="font-heading font-semibold">
                    {isSubscribed ? 'Fitxess Pro' : 'Free Plan'}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {isSubscribed 
                      ? (expiresAt 
                          ? `Active until ${new Date(expiresAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
                          : 'Active subscription')
                      : 'Upgrade for full access'
                    }
                  </p>
                </div>
              </div>
              {isSubscribed && expiresAt && (
                <span className="text-xs text-muted-foreground">
                  {willRenew ? 'Renews' : 'Expires'}: {new Date(expiresAt).toLocaleDateString()}
                </span>
              )}
            </div>
            
            {!isSubscribed ? (
              <Button
                onClick={() => showPaywall()}
                className="w-full h-11 rounded-full bg-gradient-to-r from-accent to-cyan-500 text-white font-semibold"
                data-testid="upgrade-btn"
              >
                <Crown size={18} className="mr-2" />
                Upgrade to Fitxess Pro
              </Button>
            ) : (
              <Button
                onClick={manageSubscription}
                variant="outline"
                className="w-full h-11 rounded-full"
                data-testid="manage-subscription-btn"
              >
                <CreditCard size={18} className="mr-2" />
                Manage Subscription
              </Button>
            )}

            {/* Sync Subscription — always visible. Recovery path when the
                device says one thing and the backend says another (e.g.,
                stale RC SDK cache after a sandbox subscription ended).
                Calls both restorePurchases (device) and the backend
                reconcile (server) and reports the resulting state. */}
            <Button
              onClick={handleSyncSubscription}
              disabled={syncingSubscription}
              variant="ghost"
              className="w-full h-10 rounded-full text-xs text-muted-foreground hover:text-foreground"
              data-testid="sync-subscription-btn"
            >
              {syncingSubscription ? (
                <>
                  <span className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                  Syncing...
                </>
              ) : (
                <>
                  <RefreshCw size={14} className="mr-2" />
                  Sync Subscription
                </>
              )}
            </Button>
          </div>
        </motion.div>

        {/* Notifications Section - Only shown on native mobile platforms.
            Reflects Apple's actual permission for Fitxess (re-read on mount and
            on return to the foreground); Fitxess has no separate on/off switch. */}
        {isNativePlatform && (
          <motion.div initial={{ opacity: 0, y: 0 }} animate={{ opacity: 1, y: 0 }} className="bg-card border border-border/40 rounded-xl overflow-hidden shadow-sm">
            <div className="p-6 flex items-center justify-between gap-4" data-testid="notifications-section">
              <div className="flex items-center gap-3">
                <Bell size={20} className="text-accent flex-shrink-0" />
                <div className="text-left">
                  {pushPermission === 'granted' ? (
                    <>
                      <p className="font-heading font-semibold" data-testid="notifications-enabled">Notifications Enabled</p>
                      <p className="text-sm text-muted-foreground">
                        To turn off notifications, go to iPhone Settings → Fitxess → Notifications.
                      </p>
                    </>
                  ) : pushPermission === 'denied' ? (
                    <>
                      <p className="font-heading font-semibold">Push Notifications</p>
                      <p className="text-sm text-muted-foreground" data-testid="notifications-denied">
                        Notifications are off for Fitxess. To turn them on, go to iPhone Settings → Fitxess → Notifications.
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="font-heading font-semibold">Push Notifications</p>
                      <p className="text-sm text-muted-foreground">Get meal reminders & updates</p>
                    </>
                  )}
                </div>
              </div>
              {pushPermission !== 'granted' && pushPermission !== 'denied' && (
                <Switch
                  checked={false}
                  disabled={pushPermission === 'unknown'}
                  onCheckedChange={async (checked) => {
                    if (!checked) return;
                    const granted = await enablePush();
                    if (granted) {
                      toast.success('Notifications enabled!');
                    }
                  }}
                  data-testid="notifications-toggle"
                />
              )}
            </div>
          </motion.div>
        )}

        {/* Goal Section */}
        <motion.div initial={{ opacity: 0, y: 0 }} animate={{ opacity: 1, y: 0 }} className="bg-card border border-border/40 rounded-xl overflow-hidden shadow-sm">
          <button
            onClick={() => setActiveSection(activeSection === 'goal' ? null : 'goal')}
            className="w-full p-6 flex items-center justify-between hover:bg-secondary/20 transition-colors"
            data-testid="goal-section-toggle"
          >
            <div className="flex items-center gap-3">
              <Target size={20} className="text-accent" />
              <div className="text-left">
                <p className="font-heading font-semibold">Goal</p>
                <p className="text-sm text-muted-foreground">{goalLabel(formData.goal) || 'Not set'}</p>
              </div>
            </div>
            <ChevronRight size={20} className={`transition-transform ${activeSection === 'goal' ? 'rotate-90' : ''}`} />
          </button>
          
          {activeSection === 'goal' && (
            <div className="p-6 pt-0 space-y-4">
              <Select value={formData.goal} onValueChange={(value) => setFormData({...formData, goal: value})}>
                <SelectTrigger className="h-12 bg-secondary/30 border-transparent focus:border-accent">
                  <SelectValue placeholder="Select goal" />
                </SelectTrigger>
                <SelectContent>
                  {goals.map(goal => (
                    <SelectItem key={goal} value={goal}>{goalLabel(goal)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button onClick={handleUpdateGoal} disabled={loading} className="w-full h-11 rounded-full bg-accent text-white">
                {loading ? 'Saving...' : 'Save Goal'}
              </Button>
            </div>
          )}
        </motion.div>

        {/* Body Info Section (NEW) */}
        <motion.div initial={{ opacity: 0, y: 0 }} animate={{ opacity: 1, y: 0 }} className="bg-card border border-border/40 rounded-xl overflow-hidden shadow-sm">
          <button
            onClick={() => setActiveSection(activeSection === 'body' ? null : 'body')}
            className="w-full p-6 flex items-center justify-between hover:bg-secondary/20 transition-colors"
            data-testid="body-section-toggle"
          >
            <div className="flex items-center gap-3">
              <Scale size={20} className="text-green-500" />
              <div className="text-left">
                <p className="font-heading font-semibold">Body Info</p>
                <p className="text-sm text-muted-foreground">
                  {formData.age ? `Age ${formData.age}` : ''} 
                  {formData.sex ? ` • ${formData.sex.charAt(0).toUpperCase() + formData.sex.slice(1)}` : ''}
                  {formData.height_cm || formData.heightFeet ? ` • ${getHeightDisplay()}` : ''}
                </p>
              </div>
            </div>
            <ChevronRight size={20} className={`transition-transform ${activeSection === 'body' ? 'rotate-90' : ''}`} />
          </button>
          
          {activeSection === 'body' && (
            <div className="p-6 pt-0 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Age</Label>
                  <Input
                    type="number"
                    value={formData.age}
                    onChange={(e) => setFormData({...formData, age: e.target.value})}
                    placeholder="25"
                    className="h-12 bg-secondary/30 border-transparent focus:border-accent"
                  />
                </div>
                <div>
                  <Label>Biological Sex</Label>
                  <Select value={formData.sex} onValueChange={(value) => setFormData({...formData, sex: value})}>
                    <SelectTrigger className="h-12 bg-secondary/30 border-transparent focus:border-accent">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="other">Rather not say</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Units Toggle */}
              <div className="flex items-center justify-between">
                <Label>Units</Label>
                <div className="flex items-center gap-2 bg-secondary/30 rounded-full p-1">
                  <button
                    type="button"
                    onClick={() => setFormData({...formData, units: 'metric'})}
                    className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
                      formData.units === 'metric' ? 'bg-accent text-white' : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    Metric
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({...formData, units: 'imperial'})}
                    className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
                      formData.units === 'imperial' ? 'bg-accent text-white' : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    Imperial
                  </button>
                </div>
              </div>
              
              {formData.units === 'metric' ? (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Height (cm)</Label>
                    <Input
                      type="number"
                      value={formData.height_cm}
                      onChange={(e) => setFormData({...formData, height_cm: e.target.value})}
                      placeholder="175"
                      className="h-12 bg-secondary/30 border-transparent focus:border-accent"
                    />
                  </div>
                  <div>
                    <Label>Weight (kg)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={formData.weight_kg}
                      onChange={(e) => setFormData({...formData, weight_kg: e.target.value})}
                      placeholder="70"
                      className="h-12 bg-secondary/30 border-transparent focus:border-accent"
                    />
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <Label>Height</Label>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Input
                          type="number"
                          value={formData.heightFeet}
                          onChange={(e) => setFormData({...formData, heightFeet: e.target.value})}
                          placeholder="5"
                          className="h-12 bg-secondary/30 border-transparent focus:border-accent"
                        />
                        <p className="text-xs text-muted-foreground mt-1">Feet</p>
                      </div>
                      <div>
                        <Input
                          type="number"
                          value={formData.heightInches}
                          onChange={(e) => setFormData({...formData, heightInches: e.target.value})}
                          placeholder="10"
                          className="h-12 bg-secondary/30 border-transparent focus:border-accent"
                        />
                        <p className="text-xs text-muted-foreground mt-1">Inches</p>
                      </div>
                    </div>
                  </div>
                  <div>
                    <Label>Weight (lbs)</Label>
                    <Input
                      type="number"
                      value={formData.weightLbs}
                      onChange={(e) => setFormData({...formData, weightLbs: e.target.value})}
                      placeholder="154"
                      className="h-12 bg-secondary/30 border-transparent focus:border-accent"
                    />
                  </div>
                </div>
              )}
              
              <Button onClick={handleUpdateBodyInfo} disabled={loading} className="w-full h-11 rounded-full bg-accent text-white">
                {loading ? 'Saving...' : 'Save Body Info'}
              </Button>
            </div>
          )}
        </motion.div>

        {/* Activity & Workouts Section */}
        <motion.div initial={{ opacity: 0, y: 0 }} animate={{ opacity: 1, y: 0 }} className="bg-card border border-border/40 rounded-xl overflow-hidden shadow-sm">
          <button
            onClick={() => setActiveSection(activeSection === 'activity' ? null : 'activity')}
            className="w-full p-6 flex items-center justify-between hover:bg-secondary/20 transition-colors"
            data-testid="activity-section-toggle"
          >
            <div className="flex items-center gap-3">
              <Activity size={20} className="text-orange-500" />
              <div className="text-left">
                <p className="font-heading font-semibold">Activity & Workouts</p>
                <p className="text-sm text-muted-foreground">
                  {activityLevels.find(a => a.value === formData.activity_level)?.label.split(' ')[0] || 'Not set'}
                </p>
              </div>
            </div>
            <ChevronRight size={20} className={`transition-transform ${activeSection === 'activity' ? 'rotate-90' : ''}`} />
          </button>
          
          {activeSection === 'activity' && (
            <div className="p-6 pt-0 space-y-4">
              <div>
                <Label>Activity Level</Label>
                <Select value={formData.activity_level} onValueChange={(value) => setFormData({...formData, activity_level: value})}>
                  <SelectTrigger className="h-12 bg-secondary/30 border-transparent focus:border-accent">
                    <SelectValue placeholder="Select activity level" />
                  </SelectTrigger>
                  <SelectContent>
                    {activityLevels.map(level => (
                      <SelectItem key={level.value} value={level.value}>{level.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Workout Types</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {workoutTypes.map(workout => {
                    const normalized = workout.toLowerCase().replace(/\s+/g, '_');
                    const isSelected = formData.workout_types.includes(normalized);
                    return (
                      <button
                        key={workout}
                        type="button"
                        onClick={() => toggleWorkout(workout)}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                          isSelected
                            ? 'bg-accent/10 text-accent border-2 border-accent'
                            : 'bg-secondary/30 text-foreground border-2 border-transparent'
                        }`}
                      >
                        {workout} {isSelected && <X size={12} className="inline ml-1" />}
                      </button>
                    );
                  })}
                </div>
              </div>
              <Button onClick={handleUpdateActivity} disabled={loading} className="w-full h-11 rounded-full bg-accent text-white">
                {loading ? 'Saving...' : 'Save Activity'}
              </Button>
            </div>
          )}
        </motion.div>

        {/* Macro Targets Section (NEW) */}
        <motion.div initial={{ opacity: 0, y: 0 }} animate={{ opacity: 1, y: 0 }} className="bg-card border border-border/40 rounded-xl overflow-hidden shadow-sm">
          <button
            onClick={() => setActiveSection(activeSection === 'macros' ? null : 'macros')}
            className="w-full p-6 flex items-center justify-between hover:bg-secondary/20 transition-colors"
            data-testid="macros-section-toggle"
          >
            <div className="text-left">
              <p className="font-heading font-semibold">Macro Targets</p>
              <p className="text-sm text-muted-foreground">
                {formData.target_calories}cal • {formData.target_protein_g}p • {formData.target_carbs_g}c • {formData.target_fat_g}f
              </p>
            </div>
            <ChevronRight size={20} className={`transition-transform ${activeSection === 'macros' ? 'rotate-90' : ''}`} />
          </button>
          
          {activeSection === 'macros' && (
            <div className="p-6 pt-0 space-y-4">
              {/* Auto Macros Toggle */}
              <div className="flex items-center justify-between p-4 bg-secondary/20 rounded-xl">
                <div>
                  <p className="font-medium">Auto Macros (Recommended)</p>
                  <p className="text-sm text-muted-foreground">
                    {formData.auto_macros_enabled 
                      ? 'System calculates your macros' 
                      : 'Manual editing enabled'}
                  </p>
                </div>
                <Switch
                  checked={formData.auto_macros_enabled}
                  onCheckedChange={handleToggleAutoMacros}
                  disabled={loading}
                />
              </div>
              
              {!formData.auto_macros_enabled && (
                <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg flex items-start gap-2">
                  <AlertCircle size={18} className="text-amber-500 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-amber-700">
                    Manual mode: These values will be used instead of calculated macros.
                  </p>
                </div>
              )}
              
              <div>
                <Label>Calories</Label>
                <Input
                  type="number"
                  value={formData.target_calories}
                  onChange={(e) => setFormData({...formData, target_calories: e.target.value})}
                  disabled={formData.auto_macros_enabled}
                  className={`h-12 bg-secondary/30 border-transparent focus:border-accent ${formData.auto_macros_enabled ? 'opacity-60' : ''}`}
                />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Protein (g)</Label>
                  <Input
                    type="number"
                    value={formData.target_protein_g}
                    onChange={(e) => setFormData({...formData, target_protein_g: e.target.value})}
                    disabled={formData.auto_macros_enabled}
                    className={`h-12 bg-secondary/30 border-transparent focus:border-accent ${formData.auto_macros_enabled ? 'opacity-60' : ''}`}
                  />
                </div>
                <div>
                  <Label>Carbs (g)</Label>
                  <Input
                    type="number"
                    value={formData.target_carbs_g}
                    onChange={(e) => setFormData({...formData, target_carbs_g: e.target.value})}
                    disabled={formData.auto_macros_enabled}
                    className={`h-12 bg-secondary/30 border-transparent focus:border-accent ${formData.auto_macros_enabled ? 'opacity-60' : ''}`}
                  />
                </div>
                <div>
                  <Label>Fat (g)</Label>
                  <Input
                    type="number"
                    value={formData.target_fat_g}
                    onChange={(e) => setFormData({...formData, target_fat_g: e.target.value})}
                    disabled={formData.auto_macros_enabled}
                    className={`h-12 bg-secondary/30 border-transparent focus:border-accent ${formData.auto_macros_enabled ? 'opacity-60' : ''}`}
                  />
                </div>
              </div>
              {!formData.auto_macros_enabled && (
                <MacroMismatchNotice check={manualMacroCheck} onUseDerived={applyDerivedTargetCalories} />
              )}
              {!formData.auto_macros_enabled && (
                <Button onClick={handleUpdateMacros} disabled={loading || !manualMacroCheck.consistent} data-testid="save-macro-targets-btn" className="w-full h-11 rounded-full bg-accent text-white">
                  {loading ? 'Saving...' : 'Save Macros'}
                </Button>
              )}
            </div>
          )}
        </motion.div>

        {/* Food Preferences Section */}
        <motion.div initial={{ opacity: 0, y: 0 }} animate={{ opacity: 1, y: 0 }} className="bg-card border border-border/40 rounded-xl overflow-hidden shadow-sm">
          <button
            onClick={() => setActiveSection(activeSection === 'preferences' ? null : 'preferences')}
            className="w-full p-6 flex items-center justify-between hover:bg-secondary/20 transition-colors"
            data-testid="preferences-section-toggle"
          >
            <div className="text-left">
              <p className="font-heading font-semibold flex items-center gap-2">
                Food Preferences
                {foodPrefs.dirty && (
                  <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-700" data-testid="preferences-unsaved-badge">
                    Unsaved changes
                  </span>
                )}
              </p>
              <p className="text-sm text-muted-foreground" data-testid="preferences-saved-counts">
                {foodPrefs.baseline
                  ? `${foodPrefs.baseline.allergens.length + foodPrefs.baseline.unresolved.allergens.length} allergens • ${foodPrefs.baseline.dislikes.length + foodPrefs.baseline.unresolved.dislikes.length} dislikes`
                  : 'Loading…'}
              </p>
            </div>
            <ChevronRight size={20} className={`transition-transform ${activeSection === 'preferences' ? 'rotate-90' : ''}`} />
          </button>

          {activeSection === 'preferences' && (
            <div className="p-6 pt-0 space-y-5" data-testid="preferences-section">
              {foodPrefs.status === 'loading' && (
                <p className="text-sm text-muted-foreground" data-testid="preferences-loading">Loading your food preferences…</p>
              )}
              {foodPrefs.status === 'error' && (
                <div className="text-sm text-muted-foreground" data-testid="preferences-load-error">
                  Couldn&apos;t load your food preferences.{' '}
                  <button type="button" className="text-accent underline" onClick={foodPrefs.load}>Retry</button>
                </div>
              )}
              {foodPrefs.conflictNotice && (
                <p className="text-sm rounded-lg bg-amber-500/10 text-foreground p-3" data-testid="preferences-conflict-notice">
                  Your preferences were changed on another device. Your edits are shown on top of the latest version — review and save.
                </p>
              )}
              {foodPrefs.draft && (
                <>
                  <div>
                    <Label>Allergens</Label>
                    <div className="mt-2">
                      <FoodPreferencePicker
                        purpose="allergen"
                        items={foodPrefs.draft.allergens}
                        unresolved={foodPrefs.draft.unresolved.allergens}
                        baselineKeys={foodPrefs.baselineKeys}
                        removedItems={foodPrefs.diff ? foodPrefs.diff.removed.allergens : []}
                        groups={prefGroups}
                        disabled={!foodPrefs.ready || foodPrefs.saving}
                        onAdd={(item) => foodPrefs.add('allergens', item)}
                        onRemove={(key) => foodPrefs.remove('allergens', key)}
                        onUndoRemove={(item) => foodPrefs.undoRemove('allergens', item)}
                        onRemoveUnresolved={(raw) => foodPrefs.dropUnresolved('allergens', raw)}
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Dislikes</Label>
                    <div className="mt-2">
                      <FoodPreferencePicker
                        purpose="dislike"
                        items={foodPrefs.draft.dislikes}
                        unresolved={foodPrefs.draft.unresolved.dislikes}
                        baselineKeys={foodPrefs.baselineKeys}
                        removedItems={foodPrefs.diff ? foodPrefs.diff.removed.dislikes : []}
                        groups={prefGroups}
                        disabled={!foodPrefs.ready || foodPrefs.saving}
                        onAdd={(item) => foodPrefs.add('dislikes', item)}
                        onRemove={(key) => foodPrefs.remove('dislikes', key)}
                        onUndoRemove={(item) => foodPrefs.undoRemove('dislikes', item)}
                        onRemoveUnresolved={(raw) => foodPrefs.dropUnresolved('dislikes', raw)}
                      />
                    </div>
                  </div>
                </>
              )}
              {foodPrefs.saveError && (
                <p className="text-sm text-destructive" data-testid="preferences-save-error">{foodPrefs.saveError}</p>
              )}
              {foodPrefs.dirty && (
                <p className="text-sm text-amber-700" data-testid="preferences-change-count">
                  {foodPrefs.changeCount} unsaved {foodPrefs.changeCount === 1 ? 'change' : 'changes'}
                </p>
              )}
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={foodPrefs.discard}
                  disabled={!foodPrefs.dirty || foodPrefs.saving}
                  className="flex-1 h-11 rounded-full"
                  data-testid="preferences-discard-btn"
                >
                  Discard
                </Button>
                <Button
                  onClick={handleSaveFoodPreferences}
                  disabled={!foodPrefs.dirty || !foodPrefs.ready || foodPrefs.saving}
                  className="flex-1 h-11 rounded-full bg-accent text-white"
                  data-testid="preferences-save-btn"
                >
                  {foodPrefs.saving ? 'Saving...' : 'Save Preferences'}
                </Button>
              </div>
            </div>
          )}
        </motion.div>

        <AlertDialog open={!!pendingLeave} onOpenChange={(open) => { if (!open) setPendingLeave(null); }}>
          <AlertDialogContent data-testid="preferences-leave-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Save your food preference changes?</AlertDialogTitle>
              <AlertDialogDescription>
                You have unsaved changes to your allergens or dislikes. They won&apos;t be used until you save them.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="preferences-leave-keep">Keep editing</AlertDialogCancel>
              <Button type="button" variant="outline" onClick={handleLeaveDiscard} data-testid="preferences-leave-discard">
                Discard
              </Button>
              <AlertDialogAction onClick={(e) => { e.preventDefault(); handleLeaveSave(); }} data-testid="preferences-leave-save">
                Save
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Cooking Preferences Section */}
        <motion.div initial={{ opacity: 0, y: 0 }} animate={{ opacity: 1, y: 0 }} className="bg-card border border-border/40 rounded-xl overflow-hidden shadow-sm">
          <button
            onClick={() => setActiveSection(activeSection === 'cooking' ? null : 'cooking')}
            className="w-full p-6 flex items-center justify-between hover:bg-secondary/20 transition-colors"
            data-testid="cooking-section-toggle"
          >
            <div className="flex items-center gap-3">
              <ChefHat size={20} className="text-accent" />
              <div className="text-left">
                <p className="font-heading font-semibold">Cooking Preferences</p>
                <p className="text-sm text-muted-foreground">
                  {formData.cookware.length > 0 
                    ? formData.cookware.length + ' appliances' + (formData.max_cook_time ? ' • ' + formData.max_cook_time + ' min max' : '')
                    : 'Not set'}
                </p>
              </div>
            </div>
            <ChevronRight size={20} className={`transition-transform ${activeSection === 'cooking' ? 'rotate-90' : ''}`} />
          </button>
          
          {activeSection === 'cooking' && (
            <div className="p-6 pt-0 space-y-4">
              <CookingPreferencesSelector
                selectedCookware={formData.cookware}
                onCookwareChange={(cookware) => setFormData({...formData, cookware})}
                selectedCookTime={formData.max_cook_time}
                onCookTimeChange={(time) => setFormData({...formData, max_cook_time: time})}
                showDoneButton={false}
                compact={true}
              />
              <Button onClick={handleUpdateCookingPreferences} disabled={loading} className="w-full h-11 rounded-full bg-accent text-white">
                {loading ? 'Saving...' : 'Save Cooking Preferences'}
              </Button>
            </div>
          )}
        </motion.div>

        {/* Pantry Shortcut */}
        <motion.div initial={{ opacity: 0, y: 0 }} animate={{ opacity: 1, y: 0 }} className="bg-card border border-border/40 rounded-xl overflow-hidden shadow-sm">
          <button
            onClick={() => guardedNavigate('/pantry')}
            className="w-full p-6 flex items-center justify-between hover:bg-secondary/20 transition-colors"
            data-testid="pantry-shortcut"
          >
            <div className="flex items-center gap-3">
              <Package size={20} className="text-amber-500" />
              <div className="text-left">
                <p className="font-heading font-semibold">My Pantry</p>
                <p className="text-sm text-muted-foreground">Manage ingredients you have</p>
              </div>
            </div>
            <ChevronRight size={20} />
          </button>
        </motion.div>

        {/* Admin Panel - Only shown to admins */}
        {isAdmin && (
          <motion.div initial={{ opacity: 0, y: 0 }} animate={{ opacity: 1, y: 0 }} className="bg-card border border-accent/40 rounded-xl overflow-hidden shadow-sm">
            <button
              onClick={() => guardedNavigate('/admin/meals')}
              className="w-full p-6 flex items-center justify-between hover:bg-accent/10 transition-colors"
              data-testid="admin-shortcut"
            >
              <div className="flex items-center gap-3">
                <Target size={20} className="text-accent" />
                <div className="text-left">
                  <p className="font-heading font-semibold text-accent">Admin Panel</p>
                  <p className="text-sm text-muted-foreground">Manage meals & recipes</p>
                </div>
              </div>
              <ChevronRight size={20} className="text-accent" />
            </button>
          </motion.div>
        )}

        {/* Legal / About */}
        <motion.div initial={{ opacity: 0, y: 0 }} animate={{ opacity: 1, y: 0 }} className="bg-card border border-border/40 rounded-xl overflow-hidden shadow-sm">
          <button
            onClick={() => guardedNavigate('/legal')}
            className="w-full p-6 flex items-center justify-between hover:bg-secondary/20 transition-colors"
            data-testid="legal-about-link"
          >
            <div className="flex items-center gap-3">
              <Shield size={20} className="text-blue-500" />
              <div className="text-left">
                <p className="font-heading font-semibold">Legal / About</p>
                <p className="text-sm text-muted-foreground">Health disclaimer & app info</p>
              </div>
            </div>
            <ChevronRight size={20} />
          </button>
        </motion.div>

        {/* Sources & Disclaimer */}
        <motion.div initial={{ opacity: 0, y: 0 }} animate={{ opacity: 1, y: 0 }} className="bg-card border border-border/40 rounded-xl overflow-hidden shadow-sm">
          <button
            onClick={() => guardedNavigate('/sources')}
            className="w-full p-6 flex items-center justify-between hover:bg-secondary/20 transition-colors"
            data-testid="sources-disclaimer-link"
          >
            <div className="flex items-center gap-3">
              <BookOpen size={20} className="text-emerald-500" />
              <div className="text-left">
                <p className="font-heading font-semibold">Sources &amp; Disclaimer</p>
                <p className="text-sm text-muted-foreground">Citations, how we calculate &amp; medical disclaimer</p>
              </div>
            </div>
            <ChevronRight size={20} />
          </button>
        </motion.div>

        {/* Logout Button */}
        <motion.div initial={{ opacity: 0, y: 0 }} animate={{ opacity: 1, y: 0 }}>
          <Button
            onClick={handleLogout}
            data-testid="logout-btn"
            variant="outline"
            disabled={loggingOut}
            className="w-full h-12 rounded-full font-semibold"
          >
            <LogOut size={18} className="mr-2" />
            {loggingOut ? 'Logging out...' : 'Logout'}
          </Button>
        </motion.div>

        {/* Delete Account */}
        <motion.div initial={{ opacity: 0, y: 0 }} animate={{ opacity: 1, y: 0 }}>
          <Button
            onClick={() => setShowDeleteModal(true)}
            data-testid="delete-account-btn"
            variant="ghost"
            className="w-full h-12 rounded-full font-semibold text-destructive hover:text-destructive hover:bg-destructive/10"
          >
            <Trash2 size={18} className="mr-2" />
            Delete Account
          </Button>
        </motion.div>
      </div>

      {/* Delete Account Modal */}
      <AnimatePresence>
        {showDeleteModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            style={keyboardInset ? { bottom: keyboardInset, alignItems: 'flex-start', overflowY: 'auto' } : undefined}
            data-testid="delete-account-overlay"
            onClick={() => {
              setShowDeleteModal(false);
              setDeleteConfirmText('');
            }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-card rounded-2xl p-6 max-w-sm w-full shadow-xl my-auto"
              data-testid="delete-account-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="space-y-4">
                <div className="text-center">
                  <div className="w-12 h-12 mx-auto bg-destructive/10 rounded-full flex items-center justify-center mb-4">
                    <Trash2 size={24} className="text-destructive" />
                  </div>
                  <h3 className="font-heading font-bold text-xl">Delete Account</h3>
                </div>
                
                <p className="text-muted-foreground text-center">
                  This will permanently delete your account and all associated data. This action cannot be undone.
                </p>
                
                <div className="space-y-2">
                  <Label htmlFor="delete-confirm" className="text-sm">
                    Type <span className="font-mono font-bold">DELETE</span> to confirm:
                  </Label>
                  <Input
                    id="delete-confirm"
                    data-testid="delete-confirm-input"
                    value={deleteConfirmText}
                    onChange={(e) => setDeleteConfirmText(e.target.value)}
                    placeholder="DELETE"
                    className="h-12 bg-secondary/30 border-transparent focus:border-destructive font-mono text-center"
                    autoComplete="off"
                  />
                </div>
                
                <div className="space-y-3 pt-2">
                  <Button
                    onClick={handleDeleteAccount}
                    disabled={deleting || deleteConfirmText !== 'DELETE'}
                    data-testid="confirm-delete-btn"
                    variant="destructive"
                    className="w-full h-11 rounded-full"
                  >
                    {deleting ? 'Deleting...' : 'Permanently Delete Account'}
                  </Button>
                  <Button
                    onClick={() => {
                      setShowDeleteModal(false);
                      setDeleteConfirmText('');
                    }}
                    variant="outline"
                    className="w-full h-11 rounded-full"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Regenerate Macros Modal */}
      <AnimatePresence>
        {showRegenerateModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowRegenerateModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-card rounded-2xl p-6 max-w-sm w-full shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-center space-y-4">
                <div className="w-12 h-12 mx-auto bg-accent/10 rounded-full flex items-center justify-center">
                  <Target size={24} className="text-accent" />
                </div>
                <h3 className="font-heading font-bold text-xl">Regenerate Macros?</h3>
                <p className="text-muted-foreground">
                  Would you like to recalculate your macro targets based on your updated information?
                </p>
                <div className="space-y-3 pt-2">
                  <Button
                    onClick={handleRegenerateYes}
                    disabled={loading}
                    className="w-full h-11 rounded-full bg-accent text-white"
                  >
                    {loading ? 'Calculating...' : 'Yes, Regenerate'}
                  </Button>
                  <Button
                    onClick={handleRegenerateNo}
                    variant="outline"
                    className="w-full h-11 rounded-full"
                  >
                    No, Keep Current Targets
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <BottomNav beforeNavigate={allowLeave} />
    </div>
  );
};

export default Settings;
